import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./UserPanelPages.css";
import Navbar from "../Component/Common/Navbar";
import {
  connectRealtimeSocket,
  disconnectRealtimeSocket,
} from "../Component/ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH KEYS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
];

/* ==============================
   PLANS
============================== */
const plans = [
  {
    key: "starter",
    title: "Starter Plan",
    price: "₹0",
    amount: 0,
    desc: "Good for getting started with basic domain exploration.",
    features: ["Basic search", "Limited favorites", "Starter dashboard"],
    badge: "Free",
  },
  {
    key: "pro",
    title: "Pro Plan",
    price: "₹499/mo",
    amount: 499,
    desc: "For users who want better tracking, alerts, and premium access.",
    features: [
      "Advanced search",
      "Priority favorites",
      "Notifications",
      "Faster support",
    ],
    badge: "Popular",
  },
  {
    key: "business",
    title: "Business Plan",
    price: "₹1499/mo",
    amount: 1499,
    desc: "For teams and businesses managing multiple domains and assets.",
    features: ["Team-ready", "Priority support", "Reports", "Advanced billing"],
    badge: "Business",
  },
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  disconnectRealtimeSocket();
  window.dispatchEvent(new Event("auth-state-changed"));
};

const getStoredUser = () => {
  const rawUser =
    localStorage.getItem("user") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("currentUser") ||
    sessionStorage.getItem("currentUser") ||
    "";

  if (!rawUser) return {};

  try {
    const parsed = JSON.parse(rawUser);

    if (!parsed || typeof parsed !== "object") return {};

    return parsed;
  } catch {
    return {};
  }
};

/* ==============================
   RAZORPAY SCRIPT LOADER
============================== */
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true));
      existingScript.addEventListener("error", () => resolve(false));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
};

function PlansPage() {
  const navigate = useNavigate();

  const [activePlan, setActivePlan] = useState("");
  const [loadingPlanKey, setLoadingPlanKey] = useState("");
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [status, setStatus] = useState({ type: "", message: "" });

  const redirectToLogin = useCallback(() => {
    clearAuth();
    navigate("/signin", { replace: true });
  }, [navigate]);

  /* ==============================
     PROTECTED FETCH
  ============================== */
  const authFetch = useCallback(
    async (endpoint, options = {}) => {
      const token = getToken();

      if (!token) {
        redirectToLogin();
        throw new Error("Session expired. Please login again.");
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401 || response.status === 403) {
        redirectToLogin();
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Request failed.");
      }

      return data;
    },
    [redirectToLogin]
  );

  /* ==============================
     LOAD CURRENT USER PLAN
  ============================== */
  const loadCurrentPlan = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setStatus({ type: "", message: "" });
        }

        const data = await authFetch("/auth/me");
        const user = data?.data || data?.user || {};
        const planName = String(
          user.plan || user.planName || user.plan_name || ""
        ).toLowerCase();

        const matchedPlan = plans.find(
          (plan) =>
            plan.key.toLowerCase() === planName ||
            plan.title.toLowerCase() === planName
        );

        if (matchedPlan) {
          setActivePlan(matchedPlan.key);
        }
      } catch (error) {
        if (!silent) {
          setStatus({
            type: "error",
            message: error.message || "Failed to load current plan.",
          });
        }
      }
    },
    [authFetch]
  );

  useEffect(() => {
    loadCurrentPlan();
  }, [loadCurrentPlan]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        loadCurrentPlan(true);
      }, 400);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handlePaymentStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.status === "success") {
        setStatus({
          type: "success",
          message: payload.message || "Payment completed successfully.",
        });
      }

      softRefresh();
    };

    const handleRealtimeRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleSocketAuthError = () => {
      redirectToLogin();
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("payment:status", handlePaymentStatus);
    socket.on("dashboard:update", handleRealtimeRefresh);
    socket.on("notification:new", handleRealtimeRefresh);
    socket.on("activity:new", handleRealtimeRefresh);

    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("payment:status", handlePaymentStatus);
      socket.off("dashboard:update", handleRealtimeRefresh);
      socket.off("notification:new", handleRealtimeRefresh);
      socket.off("activity:new", handleRealtimeRefresh);

      socket.off("connect_error", handleSocketAuthError);

      disconnectRealtimeSocket();
    };
  }, [loadCurrentPlan, redirectToLogin]);

  /* ==============================
     FREE PLAN ACTIVATION
  ============================== */
  const activateFreePlan = useCallback(
    async (plan) => {
      await authFetch("/payments/create", {
        method: "POST",
        body: JSON.stringify({
          planName: plan.title,
          amount: 0,
          paymentMethod: "free",
          status: "success",
          paymentId: `FREE-${Date.now()}`,
        }),
      });

      setActivePlan(plan.key);

      setStatus({
        type: "success",
        message: `${plan.title} activated successfully.`,
      });

      window.dispatchEvent(new Event("auth-state-changed"));
    },
    [authFetch]
  );

  /* ==============================
     PAID PLAN PAYMENT FLOW
  ============================== */
  const handleChoosePlan = useCallback(
    async (plan) => {
      try {
        setLoadingPlanKey(plan.key);
        setStatus({ type: "", message: "" });

        const token = getToken();

        if (!token) {
          redirectToLogin();
          return;
        }

        if (activePlan === plan.key) {
          setStatus({
            type: "success",
            message: `${plan.title} is already active.`,
          });
          return;
        }

        if (plan.amount <= 0) {
          await activateFreePlan(plan);
          return;
        }

        const scriptLoaded = await loadRazorpayScript();

        if (!scriptLoaded) {
          throw new Error("Payment gateway failed to load. Please try again.");
        }

        const productPayload = {
          productType: "plan",
          productName: plan.title,
          planKey: plan.key,
          planName: plan.title,
          amount: plan.amount,
        };

        const orderData = await authFetch("/payments/create-order", {
          method: "POST",
          body: JSON.stringify(productPayload),
        });

        if (!orderData?.order?.id || !orderData?.key) {
          throw new Error("Invalid payment order response.");
        }

        const storedUser = getStoredUser();

        const razorpayOptions = {
          key: orderData.key,
          amount: orderData.order.amount,
          currency: orderData.order.currency || "INR",
          name: "DoHostingPay",
          description: plan.title,
          order_id: orderData.order.id,

          prefill: {
            name: storedUser?.fullName || storedUser?.name || "",
            email: storedUser?.email || "",
            contact: storedUser?.phone || "",
          },

          notes: {
            productType: "plan",
            planKey: plan.key,
          },

          theme: {
            color: "#6d28d9",
          },

          modal: {
            confirm_close: true,
            ondismiss: () => {
              setLoadingPlanKey("");
              setStatus({
                type: "error",
                message: "Payment popup closed. Plan was not activated.",
              });
            },
          },

          handler: async (response) => {
            try {
              setStatus({
                type: "success",
                message: "Payment received. Verifying securely...",
              });

              await authFetch("/payments/verify", {
                method: "POST",
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  ...productPayload,
                }),
              });

              setActivePlan(plan.key);

              setStatus({
                type: "success",
                message: `${plan.title} payment successful and plan activated.`,
              });

              await loadCurrentPlan(true);

              window.dispatchEvent(new Event("auth-state-changed"));
            } catch (error) {
              setStatus({
                type: "error",
                message:
                  error.message ||
                  "Payment verification failed. Please contact support.",
              });
            } finally {
              setLoadingPlanKey("");
            }
          },
        };

        const razorpay = new window.Razorpay(razorpayOptions);

        razorpay.on("payment.failed", () => {
          setLoadingPlanKey("");
          setStatus({
            type: "error",
            message: "Payment failed. Please try again.",
          });
        });

        razorpay.open();
      } catch (error) {
        setLoadingPlanKey("");
        setStatus({
          type: "error",
          message: error.message || "Unable to start plan payment.",
        });
      }
    },
    [
      activePlan,
      activateFreePlan,
      authFetch,
      loadCurrentPlan,
      redirectToLogin,
    ]
  );

  return (
    <>
      <Navbar />

      <section className="user-page-section">
        <div className="user-page-container">
          <div className="user-page-head">
            <div>
              <h1>Plans</h1>
              <p>Choose the best plan for your domain journey.</p>

              <div className="user-realtime-row">
                <div className="user-realtime-chip">
                  <span
                    className={
                      realtimeStatus === "connected"
                        ? "user-realtime-dot online"
                        : "user-realtime-dot offline"
                    }
                  />

                  <span>
                    {realtimeStatus === "connected"
                      ? "Realtime connected"
                      : realtimeStatus === "connecting"
                      ? "Connecting realtime..."
                      : "Realtime offline"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {status.message ? (
            <div className={`user-page-alert ${status.type}`}>
              {status.message}
            </div>
          ) : null}

          <div className="user-grid-list">
            {plans.map((plan) => {
              const isActive = activePlan === plan.key;
              const isLoading = loadingPlanKey === plan.key;

              return (
                <div
                  className={`plan-card ${isActive ? "plan-card-active" : ""}`}
                  key={plan.title}
                >
                  <span className="plan-badge">{plan.badge}</span>

                  <h3>{plan.title}</h3>

                  <div className="plan-price">{plan.price}</div>

                  <p>{plan.desc}</p>

                  <div className="plan-features">
                    {plan.features.map((feature) => (
                      <span key={feature}>✔ {feature}</span>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="primary-action-btn"
                    onClick={() => handleChoosePlan(plan)}
                    disabled={isActive || Boolean(loadingPlanKey)}
                  >
                    {isActive
                      ? "✓ Active Plan"
                      : isLoading
                      ? "Processing..."
                      : plan.amount > 0
                      ? "Pay & Choose Plan"
                      : "Choose Free Plan"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

export default PlansPage;